import { defineStore } from "pinia";
import { ref, reactive, computed } from "vue";
import dailyReportService from "@/services/hrm/dailyReport.service";
import attendanceSvc from "@/services/hrm/attendanceEmployee.service";
import { toast } from "@/utils/toast";

export const useDailyReportStore = defineStore("dailyReport", () => {
  const summary = ref({
    total_present: 0,
    total_absent: 0,
    tasks_completed: 0,
    tasks_pending: 0,
  });

  const chartCategories = ref([]);
  const chartSeries = ref([]);
  const years = ref([]);
  const year = ref(new Date().getFullYear());

  const filters = reactive({
    date_from: "",
    date_to: "",
    status: "",
    department_id: "",
    sort: "recent",
    per_page: 25,
    page: 1,
  });

  const items = ref([]);
  const loading = ref(false);
  const pagination = reactive({
    total: 0,
    per_page: 25,
    current_page: 1,
  });

  const totalPages = computed(() => {
    const t = pagination.total || 0;
    const p = pagination.per_page || 25;
    return Math.max(1, Math.ceil(t / p));
  });

  async function fetchSummary(extra = {}) {
    try {
      const params = { ...filters, year: year.value, ...extra };
      const { data } = await dailyReportService.summary(params);

      summary.value = data.summary || summary.value;
      chartCategories.value = data.chart?.categories || [];
      chartSeries.value = data.chart?.series || [];
      years.value = data.meta?.years || years.value;
      year.value = data.meta?.year || year.value;
    } catch (e) {
      toast(
        e?.response?.data?.message || "Failed to load daily summary",
        "error"
      );
    }
  }

  async function fetchList(extra = {}) {
    loading.value = true;
    try {
      const params = { ...filters, ...extra };
      const res = await attendanceSvc.list(params);
      const data = res.data;

      items.value = data.data || [];
      if (data.meta) {
        pagination.total = data.meta.total;
        pagination.per_page = data.meta.per_page;
        pagination.current_page = data.meta.current_page;
      } else {
        pagination.total = items.value.length;
        pagination.per_page = params.per_page || 25;
        pagination.current_page = params.page || 1;
      }
    } catch (e) {
      toast(
        e?.response?.data?.message || "Failed to load attendance list",
        "error"
      );
    } finally {
      loading.value = false;
    }
  }

  async function reload(extra = {}) {
    filters.page = 1;
    await Promise.all([fetchSummary(extra), fetchList(extra)]);
  }

  async function exportReport(format = "xlsx") {
    const params = { ...filters, year: year.value };
    return dailyReportService.exportReport(format, params);
  }

  return {
    summary,
    chartCategories,
    chartSeries,
    years,
    year,
    filters,
    items,
    loading,
    pagination,
    totalPages,
    fetchSummary,
    fetchList,
    reload,
    exportReport,
  };
});
